// Home block — "Vous cherchez quoi ?" auto-segmentation. 4 entry points
// that cover the 4 conversion motivations for wine maps: learning, gifting,
// exploring a region, decorating. Clicking a tile drops the visitor
// straight into a filtered `/shop` view so they never face the full
// 47-product wall.
// i18n : dictionnaire inline COPY (fr/en/es/pt).
import { Link } from "react-router-dom";
import { GraduationCap, Gift, Compass, Home } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ICONS = { learn: GraduationCap, gift: Gift, region: Compass, decor: Home };
const ROUTES = { learn: "/shop?intent=learn", gift: "/shop?intent=gift", region: "/shop?intent=region", decor: "/shop?intent=decor" };
const KEYS = ["learn", "gift", "region", "decor"];

const COPY = {
  fr: {
    eyebrow: "Trouver la bonne carte", h2: "Vous cherchez quoi ?",
    see_selection: "Voir la sélection →",
    items: {
      learn: { title: "Apprendre le vin", desc: "Les cartes pédagogiques pour comprendre un vignoble sans se perdre." },
      gift: { title: "Offrir", desc: "Des cadeaux qui font mouche pour un amateur, un professionnel ou un curieux." },
      region: { title: "Découvrir une région", desc: "Explore une appellation, un terroir, un vignoble d'Europe ou du monde." },
      decor: { title: "Décorer un intérieur", desc: "Des affiches signées, pensées pour un mur de salon, un bureau ou une cave." },
    },
  },
  en: {
    eyebrow: "Find the right map", h2: "What are you looking for?",
    see_selection: "See the selection →",
    items: {
      learn: { title: "Learn wine", desc: "Educational maps to understand a vineyard without getting lost." },
      gift: { title: "Offer a gift", desc: "Gifts that land — for an enthusiast, a professional or the merely curious." },
      region: { title: "Discover a region", desc: "Explore an appellation, a terroir, a vineyard in Europe or beyond." },
      decor: { title: "Decorate an interior", desc: "Signed posters, designed for a living-room wall, an office or a cellar." },
    },
  },
  es: {
    eyebrow: "Encontrar el mapa adecuado", h2: "¿Qué estás buscando?",
    see_selection: "Ver la selección →",
    items: {
      learn: { title: "Aprender vino", desc: "Los mapas pedagógicos para comprender un viñedo sin perderse." },
      gift: { title: "Regalar", desc: "Regalos que aciertan para un aficionado, un profesional o un curioso." },
      region: { title: "Descubrir una región", desc: "Explora una denominación, un terruño, un viñedo de Europa o del mundo." },
      decor: { title: "Decorar un interior", desc: "Pósters firmados, pensados para una pared de salón, oficina o bodega." },
    },
  },
  pt: {
    eyebrow: "Encontrar o mapa certo", h2: "O que procura?",
    see_selection: "Ver a seleção →",
    items: {
      learn: { title: "Aprender vinho", desc: "Os mapas pedagógicos para compreender um vinhedo sem se perder." },
      gift: { title: "Oferecer", desc: "Presentes que acertam para um apreciador, um profissional ou um curioso." },
      region: { title: "Descobrir uma região", desc: "Explore uma denominação, um terroir, um vinhedo da Europa ou do mundo." },
      decor: { title: "Decorar um interior", desc: "Posters assinados, pensados para uma parede de sala, escritório ou adega." },
    },
  },
};

const WhatAreYouLookingForBlock = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <section className="py-16 md:py-24 bg-[color:var(--gv-mist)]" data-testid="home-looking-for">
      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center mb-10">
          <div className="inline-block text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">
            {c.eyebrow}
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">
            {c.h2}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {KEYS.map((key) => {
            const Icon = ICONS[key];
            const item = c.items[key];
            return (
              <Link
                key={key}
                to={ROUTES[key]}
                className="group bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6 hover:border-[color:var(--gv-pink)] hover:shadow-lg transition-all"
                data-testid={`looking-for-${key}`}
              >
                <Icon size={22} className="text-[color:var(--gv-pink)] mb-3" />
                <div className="font-display text-lg text-[color:var(--gv-ink)] mb-1.5">{item.title}</div>
                <p className="text-[13px] text-[color:var(--gv-ink)]/65 leading-snug">{item.desc}</p>
                <div className="text-[color:var(--gv-pink)] text-[12px] mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  {c.see_selection}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatAreYouLookingForBlock;
